import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  Clock,
  Compass,
  Eye,
  ExternalLink,
  FlaskConical,
  GraduationCap,
  HelpCircle,
  Layers,
  ListOrdered,
  type LucideIcon,
  MessageCircle,
  Minus,
  Server,
  Shield,
  Swords,
  Target,
} from 'lucide-react';
import Carousel from '../../ui/Carousel';
import { AgentClickPayload } from '../AiAgentChat';
import {
  GtmCompetitor,
  GtmComparisonFeature,
  GtmComparisonVendor,
  GtmContentListItem,
  GtmContentListItemKind,
  GtmContentSection,
  GtmDemo,
  GtmEvaluationStep,
  GtmFaq,
  GtmLab,
  GtmRecognition,
  GtmSalesStep,
  GtmStat,
} from '../../../types/gtm-architecture';
import {
  ArchitectureOverride,
  COMPONENT_ACCENT_CYCLE,
  DEFAULT_LIST_LABELS,
} from './architectureOverrides';

/**
 * Section renderers for the generic architecture page.
 *
 * Each one takes a slice of the detailed `go-to-market` payload and renders it in the layout
 * the Hybrid Mesh Firewall page established. Nothing here holds copy: labels that aren't
 * structural ('QUALIFYING QUESTIONS', a nav item's name) come from the content row or the
 * per-slug override.
 */

/** A container section owns a page section: its nav entry, badge icon, and fallback badge text. */
export interface ArchitectureContainer {
  navLabel: string;
  icon: LucideIcon;
  badge: string;
  /** Container sections that are page furniture rather than nav destinations. */
  inNav?: boolean;
}

export const ARCHITECTURE_CONTAINERS: Record<string, ArchitectureContainer> = {
  hero: { navLabel: 'Overview', icon: Shield, badge: 'CISCO SECURITY', inNav: false },
  architecture: { navLabel: 'Architecture', icon: Shield, badge: 'ARCHITECTURE', inNav: false },
  discovery: { navLabel: 'Discovery & Brief', icon: Compass, badge: 'DISCOVERY & SOLUTION BRIEF' },
  usecases: { navLabel: 'Use Cases', icon: Target, badge: 'USE CASES' },
  competitive: { navLabel: 'Competitive Insights', icon: Swords, badge: 'COMPETITIVE INSIGHTS' },
  pov: { navLabel: 'POV', icon: Eye, badge: 'POV' },
  'eval-process': { navLabel: 'Eval Process', icon: FlaskConical, badge: 'EVAL PROCESS' },
  'sales-process': { navLabel: 'Sales Process', icon: ListOrdered, badge: 'SALES PROCESS GUIDANCE' },
  products: { navLabel: 'Products', icon: Layers, badge: 'PRODUCTS' },
  learn: { navLabel: 'Learn', icon: GraduationCap, badge: 'LEARN' },
  faq: { navLabel: 'FAQ', icon: HelpCircle, badge: 'FAQ' },
  cta: { navLabel: 'Next Steps', icon: ArrowRight, badge: '', inNav: false },
};

/** Brief sections render as cards inside their container rather than as sections of their own. */
export const BRIEF_PARENTS: Record<string, string> = {
  'discovery-guide': 'discovery',
  'solution-brief': 'discovery',
  'use-case-brief': 'usecases',
  'competitive-brief': 'competitive',
  'product-brief': 'products',
};

export const byOrder = <T extends { order?: number; id?: number }>(rows?: T[]): T[] =>
  [...(rows || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id ?? 0) - (b.id ?? 0));

const listItemsOfKind = (section: GtmContentSection, kind: GtmContentListItemKind) =>
  byOrder(section.list_items).filter((li) => li.kind === kind);

const HEADING_GRADIENT = {
  background: 'linear-gradient(135deg, #ffffff, #8eafc8)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
} as const;

/** Badge chip + heading + intro, the header every page section opens with. */
export const SectionHeader: React.FC<{
  badge?: string | null;
  icon: LucideIcon;
  heading?: string | null;
  intro?: string | null;
  /** The Learn section badge is green rather than Cisco blue. */
  badgeColor?: string;
  plainHeading?: boolean;
}> = ({ badge, icon: Icon, heading, intro, badgeColor = '#049fd9', plainHeading = false }) => (
  <>
    {badge && (
      <div className="text-center mb-4">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium uppercase"
          style={{
            borderWidth: 1,
            borderColor: `${badgeColor}4d`,
            backgroundColor: `${badgeColor}1a`,
            color: badgeColor,
          }}
        >
          <Icon className="w-4 h-4" />
          {badge}
        </div>
      </div>
    )}
    {heading && (
      <h2
        className={`text-4xl font-bold text-center mb-3 ${plainHeading ? 'text-white' : ''}`}
        style={plainHeading ? undefined : HEADING_GRADIENT}
      >
        {heading}
      </h2>
    )}
    {intro && <p className="text-[#8eafc8] text-center max-w-2xl mx-auto mb-12">{intro}</p>}
  </>
);

const ClickHint: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="text-[#8eafc8] text-xs font-bold tracking-wide">{label}</div>
    <div className="text-[#3a5068] text-[10px] font-medium inline-flex items-center gap-1">
      <MessageCircle className="w-3 h-3" /> click to role-play
    </div>
  </div>
);

/** Sales-enablement links: label in `text`, URL in `response`. */
export const ResourceLinkRow: React.FC<{ items: GtmContentListItem[]; label: string }> = ({
  items,
  label,
}) => {
  if (!items.length) return null;
  return (
    <div className="pt-2">
      <div className="text-[#6b8299] text-xs font-bold tracking-wide mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) =>
          item.response ? (
            <a
              key={item.id}
              href={item.response}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-[#8eafc8] hover:border-[#049fd9]/40 hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              {item.text} <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span
              key={item.id}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-[#8eafc8]"
            >
              {item.text}
            </span>
          )
        )}
      </div>
    </div>
  );
};

/**
 * A sourced brief card — the qualifying questions, positioning points, and citations that make
 * up a discovery guide, solution brief, use-case brief, competitive brief or product brief.
 * Every bullet is a button so an SE can hand it to the AI agent for role-play.
 */
export const SourcedContentCard: React.FC<{
  section: GtmContentSection;
  listLabels?: ArchitectureOverride['listLabels'];
  onItemClick: (ctx: AgentClickPayload) => void;
}> = ({ section, listLabels, onItemClick }) => {
  const label = (kind: GtmContentListItemKind) =>
    listLabels?.[kind] || DEFAULT_LIST_LABELS[kind];
  const sectionLabel = section.eyebrow || section.heading || undefined;
  const points = byOrder(section.points);
  const questions = listItemsOfKind(section, 'question');
  const listenFor = listItemsOfKind(section, 'listen_for');
  const challenges = listItemsOfKind(section, 'challenge');
  const objections = listItemsOfKind(section, 'objection');
  const internalResources = listItemsOfKind(section, 'internal_resource');
  const sources = byOrder(section.sources);

  return (
    <div className="bg-[#0f2035] rounded-2xl border border-white/10 p-6 sm:p-8 mb-12">
      {section.eyebrow && (
        <div className="text-[#049fd9] text-xs font-bold tracking-widest mb-2">{section.eyebrow}</div>
      )}
      {section.heading && <h3 className="text-white font-bold text-xl mb-3">{section.heading}</h3>}
      {section.intro && (
        <p className="text-[#8eafc8] text-sm leading-relaxed mb-6 max-w-3xl">{section.intro}</p>
      )}

      {questions.length > 0 && (
        <div className="mb-6">
          <ClickHint label={label('question')} />
          <ul className="space-y-2">
            {questions.map((q) => (
              <li key={q.id}>
                <button
                  type="button"
                  onClick={() => onItemClick({ kind: 'question', text: q.text, sectionLabel })}
                  className="w-full flex items-start gap-2 text-sm text-[#c9d8e6] text-left rounded-lg -mx-2 px-2 py-1 hover:bg-white/5 hover:text-white transition-colors cursor-pointer"
                >
                  <span className="text-[#049fd9] font-bold shrink-0">Q.</span>
                  <span>{q.text}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {listenFor.length > 0 && (
        <div className="mb-6">
          <ClickHint label={label('listen_for')} />
          <ul className="space-y-2">
            {listenFor.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => onItemClick({ kind: 'listenFor', text: l.text, sectionLabel })}
                  className="w-full flex items-start gap-2 text-sm text-[#c9d8e6] text-left rounded-lg -mx-2 px-2 py-1 hover:bg-white/5 hover:text-white transition-colors cursor-pointer"
                >
                  <Check className="w-4 h-4 text-[#34d399] mt-0.5 shrink-0" />
                  <span>{l.text}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {points.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {points.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() =>
                onItemClick({
                  kind: 'point',
                  text: p.title,
                  detail: p.description || undefined,
                  sectionLabel,
                })
              }
              className="text-left bg-[#0b1929] border border-white/5 rounded-xl p-4 hover:border-[#049fd9]/40 hover:bg-[#0d1f33] transition-colors cursor-pointer"
            >
              <div className="text-sm text-[#d0e4f4] font-semibold mb-1">{p.title}</div>
              {p.description && (
                <div className="text-xs text-[#8eafc8] leading-relaxed">{p.description}</div>
              )}
            </button>
          ))}
        </div>
      )}

      {challenges.length > 0 && (
        <div className="mt-6">
          <ClickHint label={label('challenge')} />
          <ul className="space-y-2">
            {challenges.map((ch) => (
              <li key={ch.id}>
                <button
                  type="button"
                  onClick={() => onItemClick({ kind: 'challenge', text: ch.text, sectionLabel })}
                  className="w-full flex items-start gap-2 text-sm text-[#c9d8e6] text-left rounded-lg -mx-2 px-2 py-1 hover:bg-white/5 hover:text-white transition-colors cursor-pointer"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] mt-1.5 flex-shrink-0" />
                  <span>{ch.text}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {objections.length > 0 && (
        <div className="mt-6">
          <ClickHint label={label('objection')} />
          <div className="space-y-3">
            {objections.map((o) => (
              <button
                type="button"
                key={o.id}
                onClick={() =>
                  onItemClick({
                    kind: 'challenge',
                    text: o.text,
                    detail: o.response || undefined,
                    sectionLabel,
                  })
                }
                className="w-full text-left bg-[#0b1929] border border-white/5 rounded-xl p-4 hover:border-[#049fd9]/40 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-2 text-sm text-[#d0e4f4] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] mt-1.5 flex-shrink-0" />
                  <span>{o.text}</span>
                </div>
                {o.response && (
                  <p className="text-xs text-[#8eafc8] leading-relaxed mt-2 pl-3.5">{o.response}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {internalResources.length > 0 && (
        <div className="mt-6">
          <ResourceLinkRow items={internalResources} label={label('internal_resource')} />
        </div>
      )}

      {sources.length > 0 && (
        <div className="pt-4 mt-6 border-t border-white/5">
          <div className="text-[#6b8299] text-xs font-bold tracking-wide mb-2">SOURCES</div>
          <div className="flex flex-wrap gap-2">
            {sources.map((s) =>
              s.url ? (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-[#8eafc8] hover:border-[#049fd9]/40 hover:text-white transition-colors inline-flex items-center gap-1.5"
                >
                  {s.label} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <span
                  key={s.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-white/10 text-[#8eafc8]"
                >
                  {s.label}
                </span>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const StatsBand: React.FC<{ stats: GtmStat[] }> = ({ stats }) => {
  if (!stats.length) return null;
  return (
    <section className="py-12 border-b border-white/5">
      <div className="max-w-[1120px] mx-auto px-5 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {byOrder(stats).map((s) => (
            <div key={s.id} className="text-center">
              <div className="text-3xl md:text-4xl font-extrabold text-white mb-1">
                {s.prefix}
                {s.value}
                {s.suffix}
              </div>
              <div className="text-[#6b8299] text-xs font-bold tracking-wide uppercase">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const RecognitionsBand: React.FC<{ recognitions: GtmRecognition[] }> = ({
  recognitions,
}) => {
  if (!recognitions.length) return null;
  return (
    <section className="py-20 border-b border-white/5">
      <div className="max-w-[1120px] mx-auto px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {byOrder(recognitions).map((r) => {
            const body = (
              <>
                <div className="text-[#049fd9] text-xs font-bold tracking-widest mb-2">{r.org}</div>
                <p className="text-[#c9d8e6] text-sm leading-relaxed">{r.title}</p>
              </>
            );
            return r.url ? (
              <a
                key={r.id}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#0f2035] rounded-xl border border-white/10 p-5 hover:border-[#049fd9]/40 transition-colors"
              >
                {body}
              </a>
            ) : (
              <div key={r.id} className="bg-[#0f2035] rounded-xl border border-white/10 p-5">
                {body}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/** Architecture cards, for slugs with no diagram image in their override. */
export const ComponentCards: React.FC<{
  components: any[];
  override: ArchitectureOverride;
}> = ({ components, override }) => {
  if (!components.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {byOrder(components).map((c: any, i: number) => {
        const Icon = override.componentIcons?.[c.name] || Server;
        const accent =
          override.componentAccents?.[c.name] ||
          COMPONENT_ACCENT_CYCLE[i % COMPONENT_ACCENT_CYCLE.length];
        return (
          <div
            key={c.id ?? i}
            className="bg-[#0f2035] rounded-xl border border-white/10 p-6 hover:border-white/20 transition-colors"
          >
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center mb-5"
              style={{ backgroundColor: `${accent}26` }}
            >
              <Icon className="w-5 h-5" style={{ color: accent }} />
            </div>
            <h3 className="text-white font-bold text-lg mb-2">{c.name}</h3>
            <p className="text-[#6b8299] text-sm leading-relaxed">
              {c.description || c.short_description}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const tagValues = (uc: any): string[] =>
  (uc.tag || uc.tags || [])
    .map((t: any) => (typeof t === 'object' ? t.value || t.name : t))
    .filter(Boolean);

export const UseCaseCarousel: React.FC<{ useCases: any[]; tabs?: string[] }> = ({
  useCases,
  tabs,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const ordered = byOrder(useCases);
  const visible = activeTab
    ? ordered.filter((uc: any) => tagValues(uc).includes(activeTab))
    : ordered;

  return (
    <>
      {tabs && tabs.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
          <button
            type="button"
            onClick={() => setActiveTab(null)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold border capitalize transition-colors ${
              activeTab === null
                ? 'bg-[#049fd9] border-[#049fd9] text-white'
                : 'bg-transparent border-white/15 text-[#8eafc8] hover:border-[#049fd9]/50 hover:text-white'
            }`}
          >
            All
          </button>
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab((a) => (a === t ? null : t))}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border capitalize transition-colors ${
                activeTab === t
                  ? 'bg-[#049fd9] border-[#049fd9] text-white'
                  : 'bg-transparent border-white/15 text-[#8eafc8] hover:border-[#049fd9]/50 hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="text-center text-[#6b8299] py-12">No use cases match the selected tag.</div>
      ) : (
        <Carousel
          theme="dark"
          ariaLabel="Use cases"
          itemClassName="w-[85%] sm:w-[60%] lg:w-[40%] xl:w-[32%]"
          showDots={false}
          showProgressBar
        >
          {visible.map((uc: any, i: number) => (
            <div
              key={uc.id ?? i}
              className="h-full bg-[#0f2035] rounded-xl border border-white/10 p-6 hover:border-[#049fd9]/40 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-lg bg-[#1a2d42] flex items-center justify-center mb-5">
                <Target className="w-5 h-5 text-[#8eafc8]" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{uc.name}</h3>
              <p className="text-[#6b8299] text-sm leading-relaxed mb-5">
                {uc.description || uc.short_description}
              </p>
              <div className="flex flex-wrap gap-2">
                {tagValues(uc).map((tag: string, ti: number) => (
                  <span
                    key={ti}
                    className="px-3 py-1 bg-[#1a2d42] rounded-full text-[#8eafc8] text-xs font-medium border border-white/5 capitalize"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Carousel>
      )}
    </>
  );
};

const SupportPill: React.FC<{ supported?: boolean }> = ({ supported }) =>
  supported ? (
    <span className="inline-flex w-7 h-7 rounded-full bg-[#10b981]/20 items-center justify-center">
      <Check className="w-4 h-4 text-[#34d399]" />
    </span>
  ) : (
    <span className="inline-flex w-7 h-7 rounded-full bg-white/5 items-center justify-center">
      <Minus className="w-4 h-4 text-[#3a5068]" />
    </span>
  );

/**
 * Capability matrix. Columns come from the motion's ordered comparison vendors; each row's
 * cells are matched to a column on `comparison_vendor` id rather than on position, so a
 * missing cell renders as unsupported instead of shifting the row.
 */
export const ComparisonTable: React.FC<{
  vendors: GtmComparisonVendor[];
  features: GtmComparisonFeature[];
}> = ({ vendors, features }) => {
  const columns = byOrder(vendors);
  if (!columns.length || !features.length) return null;

  return (
    <div className="bg-[#0f2035] rounded-2xl border border-white/10 overflow-hidden mb-12">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-6 py-4 text-[#6b8299] text-xs font-bold tracking-widest">
                CAPABILITY
              </th>
              {columns.map((v) => (
                <th
                  key={v.id}
                  className={`text-center px-4 py-4 text-xs font-bold tracking-widest uppercase ${
                    v.is_cisco ? 'text-[#049fd9]' : 'text-[#6b8299]'
                  }`}
                >
                  {v.vendor_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byOrder(features).map((feat) => {
              const byVendor: Record<number, boolean> = {};
              (feat.values || []).forEach((val) => {
                byVendor[val.comparison_vendor] = val.supported;
              });
              return (
                <tr key={feat.id} className="border-b border-white/5 last:border-0">
                  <td className="px-6 py-4 text-white text-sm font-medium">{feat.name}</td>
                  {columns.map((v) => (
                    <td key={v.id} className="text-center px-4 py-4">
                      <SupportPill supported={byVendor[v.id]} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const CompetitorCards: React.FC<{ competitors: GtmCompetitor[] }> = ({ competitors }) => {
  if (!competitors.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {byOrder(competitors).map((comp) => {
        const points = byOrder(comp.points);
        const weaknesses = points.filter((p) => p.kind === 'weakness');
        const edges = points.filter((p) => p.kind === 'cisco_edge');
        return (
          <div key={comp.id} className="bg-[#0f2035] rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between gap-3 mb-5">
              <h3 className="text-white font-bold text-lg">{comp.display_name || comp.vendor_name}</h3>
              {comp.market_share && (
                <span className="text-[#6b8299] text-xs font-semibold shrink-0">
                  {comp.market_share}
                </span>
              )}
            </div>

            {weaknesses.length > 0 && (
              <div className="mb-5">
                <div className="text-[#f87171] text-xs font-bold tracking-wider mb-3">WEAKNESSES</div>
                <ul className="space-y-2">
                  {weaknesses.map((w) => (
                    <li key={w.id} className="flex items-center text-sm text-[#8eafc8]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f87171] mr-2.5 flex-shrink-0" />
                      {w.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {edges.length > 0 && (
              <div className="bg-[#1a2d42] rounded-lg p-4 border border-white/5">
                <div className="text-[#049fd9] text-xs font-bold tracking-wider mb-3">
                  CISCO&apos;S EDGE
                </div>
                <ul className="space-y-2">
                  {edges.map((e) => (
                    <li key={e.id} className="flex items-center text-sm text-[#8eafc8]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#049fd9] mr-2.5 flex-shrink-0" />
                      {e.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Cisco's stance on the motion — the `pov` section's own points, as a clickable grid. */
export const PointGrid: React.FC<{
  points: GtmContentSection['points'];
  sectionLabel: string;
  onItemClick: (ctx: AgentClickPayload) => void;
}> = ({ points, sectionLabel, onItemClick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {byOrder(points).map((p) => (
      <button
        type="button"
        key={p.id}
        onClick={() =>
          onItemClick({
            kind: 'point',
            text: p.title,
            detail: p.description || undefined,
            sectionLabel,
          })
        }
        className="text-left bg-[#0f2035] border border-white/10 rounded-xl p-5 hover:border-[#049fd9]/40 hover:bg-[#122540] transition-colors cursor-pointer"
      >
        <div className="text-sm text-[#d0e4f4] font-semibold mb-1">{p.title}</div>
        {p.description && (
          <div className="text-xs text-[#8eafc8] leading-relaxed">{p.description}</div>
        )}
      </button>
    ))}
  </div>
);

export const EvaluationSteps: React.FC<{
  steps: GtmEvaluationStep[];
  onItemClick: (ctx: AgentClickPayload) => void;
}> = ({ steps, onItemClick }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
    {byOrder(steps).map((step, i) => (
      <button
        type="button"
        key={step.id}
        onClick={() =>
          onItemClick({
            kind: 'point',
            text: step.title,
            detail: step.description || undefined,
            sectionLabel: `Eval Process — ${step.phase || step.title}`,
          })
        }
        className="text-left bg-[#0f2035] rounded-xl border border-white/10 p-6 relative hover:border-[#049fd9]/40 hover:bg-[#122540] transition-colors cursor-pointer"
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-[#3a5068] text-sm font-mono">{String(i + 1).padStart(2, '0')}</span>
          {step.phase && (
            <span className="text-[#049fd9] text-[11px] font-bold uppercase tracking-wide">
              {step.phase}
            </span>
          )}
        </div>
        <h3 className="text-white font-bold text-lg mb-2">{step.title}</h3>
        {step.description && (
          <p className="text-[#8eafc8] text-sm leading-relaxed">{step.description}</p>
        )}
      </button>
    ))}
  </div>
);

/**
 * The sales process as a stacked deck: the active step is a full card, upcoming steps peek out
 * behind it as thin number+title bars that can be clicked to jump ahead.
 */
export const SalesProcessDeck: React.FC<{ steps: GtmSalesStep[] }> = ({ steps }) => {
  const [activeStep, setActiveStep] = useState(0);
  const ordered = [...steps].sort(
    (a, b) => (a.step_number ?? a.order ?? 0) - (b.step_number ?? b.order ?? 0)
  );
  if (!ordered.length) return null;
  const active = Math.min(activeStep, ordered.length - 1);

  return (
    <>
      <div className="flex items-center justify-center gap-1.5 mb-8 flex-wrap">
        {ordered.map((s, i) => (
          <div key={s.id} className="flex items-center">
            <button
              type="button"
              onClick={() => setActiveStep(i)}
              aria-current={i === active ? 'true' : undefined}
              className={`w-8 h-8 rounded-full text-xs font-bold flex items-center justify-center shrink-0 border transition-colors ${
                i === active
                  ? 'bg-[#049fd9] border-[#049fd9] text-white'
                  : i < active
                  ? 'bg-[#049fd9]/15 border-[#049fd9]/40 text-[#049fd9]'
                  : 'bg-transparent border-white/15 text-[#6b8299] hover:border-white/30'
              }`}
            >
              {i < active ? <Check className="w-3.5 h-3.5" /> : s.step_number || i + 1}
            </button>
            {i < ordered.length - 1 && (
              <div className={`w-5 sm:w-8 h-px ${i < active ? 'bg-[#049fd9]/50' : 'bg-white/10'}`} />
            )}
          </div>
        ))}
      </div>

      <div
        className="relative max-w-2xl mx-auto mb-2"
        style={{ paddingBottom: `${Math.min(ordered.length - 1 - active, 3) * 16 + 8}px` }}
      >
        {ordered.map((s, i) => {
          const rel = i - active;
          if (rel < 0) return null;
          const isActive = rel === 0;
          const depth = Math.min(rel, 3);
          return (
            <div
              key={s.id}
              onClick={() => !isActive && setActiveStep(i)}
              className={`${isActive ? 'relative' : 'absolute inset-x-0 bottom-0'} rounded-2xl border transition-all duration-300 ease-out ${
                isActive
                  ? 'bg-[#0f2035] border-[#049fd9]/40 shadow-[0_16px_40px_rgba(0,0,0,0.35)] p-7 z-20'
                  : 'bg-[#132840] border-white/10 p-4 cursor-pointer hover:border-[#049fd9]/40 hover:brightness-110'
              }`}
              style={
                isActive
                  ? undefined
                  : {
                      bottom: `${depth * 16}px`,
                      zIndex: 10 - depth,
                      transform: `scale(${1 - depth * 0.035})`,
                      opacity: rel > 3 ? 0 : 1,
                      pointerEvents: rel > 3 ? 'none' : 'auto',
                    }
              }
            >
              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full flex items-center justify-center shrink-0 font-bold ${
                    isActive
                      ? 'w-9 h-9 bg-[#049fd9]/15 border border-[#049fd9]/30 text-[#049fd9] text-sm'
                      : 'w-7 h-7 bg-white/5 border border-white/10 text-[#8eafc8] text-xs'
                  }`}
                >
                  {s.step_number || i + 1}
                </span>
                <h3
                  className={
                    isActive ? 'text-white font-bold text-xl' : 'text-[#c9d8e6] font-semibold text-sm'
                  }
                >
                  {s.title}
                </h3>
              </div>
              {isActive && (
                <div className="mt-4 pl-12 space-y-4">
                  {s.goal && <p className="text-[#8eafc8] text-sm leading-relaxed">{s.goal}</p>}

                  {byOrder(s.actions).length > 0 && (
                    <div>
                      <div className="text-[#049fd9] text-xs font-bold tracking-wide mb-2">
                        WHAT TO DO
                      </div>
                      <ul className="space-y-1.5">
                        {byOrder(s.actions).map((a) => (
                          <li
                            key={a.id}
                            className="flex items-start gap-2 text-sm text-[#c9d8e6] leading-relaxed"
                          >
                            <Check className="w-3.5 h-3.5 text-[#34d399] mt-0.5 flex-shrink-0" />
                            <span>{a.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {s.talk_track && (
                    <div className="bg-[#0b1929] border-l-2 border-[#049fd9] rounded-r-lg px-4 py-3">
                      <div className="text-[#6b8299] text-[10px] font-bold tracking-wide mb-1">
                        TALK TRACK
                      </div>
                      <p className="text-sm text-[#8eafc8] italic leading-relaxed">
                        &quot;{s.talk_track}&quot;
                      </p>
                    </div>
                  )}

                  {s.exit_criteria && (
                    <div className="flex items-start gap-2 text-xs text-[#6b8299] leading-relaxed">
                      <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#049fd9]" />
                      <span>
                        <span className="text-[#8eafc8] font-semibold">Move on when:</span>{' '}
                        {s.exit_criteria}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between max-w-2xl mx-auto mb-10">
        <button
          type="button"
          onClick={() => setActiveStep((n) => Math.max(0, n - 1))}
          disabled={active === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-[#8eafc8] hover:text-white disabled:opacity-30 disabled:hover:text-[#8eafc8] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <span className="text-[#6b8299] text-xs">
          Step {active + 1} of {ordered.length}
        </span>
        <button
          type="button"
          onClick={() => setActiveStep((n) => Math.min(ordered.length - 1, n + 1))}
          disabled={active === ordered.length - 1}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-[#8eafc8] hover:text-white disabled:opacity-30 disabled:hover:text-[#8eafc8] transition-colors"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );
};

export const LearnGrid: React.FC<{ labs: GtmLab[]; demos: GtmDemo[] }> = ({ labs, demos }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
    {labs.length > 0 && (
      <div>
        <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#fbbf24]" />
          Interactive Labs
        </h3>
        <div className="space-y-4">
          {byOrder(labs).map((lab) => (
            <div key={lab.id} className="bg-[#0f2035] rounded-xl border border-white/10 p-5">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h4 className="text-white font-semibold">{lab.title}</h4>
                {lab.status && (
                  <span className="text-[#34d399] text-xs font-bold tracking-wide shrink-0">
                    {lab.status}
                  </span>
                )}
              </div>
              {lab.description && <p className="text-[#6b8299] text-sm mb-4">{lab.description}</p>}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-[#6b8299] text-sm">
                  {lab.duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {lab.duration}
                    </span>
                  )}
                  {lab.difficulty && (
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5" /> {lab.difficulty}
                    </span>
                  )}
                </div>
                {lab.url ? (
                  <a
                    href={lab.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#049fd9] text-sm font-semibold hover:text-[#16bdeb] inline-flex items-center gap-1"
                  >
                    Start Lab <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="text-[#3a5068] text-sm font-semibold">Start Lab</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {demos.length > 0 && (
      <div>
        <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#049fd9]" />
          Live Demonstrations
        </h3>
        <div className="space-y-4">
          {byOrder(demos).map((demo) => (
            <div key={demo.id} className="bg-[#0f2035] rounded-xl border border-white/10 p-5">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h4 className="text-white font-semibold">{demo.title}</h4>
                {demo.status && (
                  <span className="text-[#34d399] text-xs font-bold tracking-wide shrink-0">
                    {demo.status}
                  </span>
                )}
              </div>
              {demo.description && <p className="text-[#6b8299] text-sm mb-4">{demo.description}</p>}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-[#6b8299] text-sm">
                  <Clock className="w-3.5 h-3.5" /> {demo.updated_label || demo.duration}
                </span>
                {demo.url && (
                  <a
                    href={demo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#049fd9] text-sm font-semibold hover:text-[#16bdeb] inline-flex items-center gap-1"
                  >
                    Watch Demo <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export const FaqAccordion: React.FC<{ faqs: GtmFaq[] }> = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const ordered = byOrder(faqs);
  if (!ordered.length) return null;

  return (
    <div className="space-y-3">
      {ordered.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={item.id} className="bg-[#0f2035] border border-white/10 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
            >
              <span className="text-white font-semibold text-sm">{item.question}</span>
              <ChevronDown
                className={`w-4 h-4 text-[#8eafc8] flex-shrink-0 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isOpen && item.answer && (
              <div className="px-5 pb-4">
                <p className="text-[#8eafc8] text-sm leading-relaxed">{item.answer}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Placeholders shaped like the real cards, so the layout doesn't jump when data lands. */
export const SkeletonCards: React.FC<{ count: number; cols?: string }> = ({ count, cols = '' }) => (
  <div className={`grid grid-cols-1 ${cols} gap-5`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-[#0f2035] rounded-xl border border-white/10 p-6 animate-pulse">
        <div className="w-11 h-11 rounded-lg bg-white/5 mb-5" />
        <div className="h-4 w-2/3 bg-white/10 rounded mb-3" />
        <div className="h-3 w-full bg-white/5 rounded mb-2" />
        <div className="h-3 w-5/6 bg-white/5 rounded" />
      </div>
    ))}
  </div>
);

/**
 * The motion exists but carried no content rows. Distinct from `DataError`: the request
 * succeeded, so the gap is the content itself — either the motion hasn't been seeded, or the
 * API this UI points at is running a build that predates the `go_to_market_*` content tables.
 */
export const NoContent: React.FC<{ name?: string; onRetry: () => void }> = ({ name, onRetry }) => (
  <div className="bg-[#0f2035] border border-white/10 rounded-xl p-8 text-center">
    <p className="text-white font-semibold mb-1">
      No architecture content published for {name || 'this motion'} yet.
    </p>
    <p className="text-[#8eafc8] text-sm mb-4">
      The page builds itself from the motion&apos;s content sections, and this response carried
      none. If the content has been seeded, the API this app is pointed at may not be serving the
      content tables yet.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#049fd9] text-white hover:bg-[#0380b0] transition-colors"
    >
      Retry
    </button>
  </div>
);

export const DataError: React.FC<{ label: string; onRetry: () => void }> = ({ label, onRetry }) => (
  <div className="bg-[#ef4444]/5 border border-[#ef4444]/20 rounded-xl p-8 text-center">
    <p className="text-[#fca5a5] font-semibold mb-1">Couldn&apos;t load {label} from the backend.</p>
    <p className="text-[#8eafc8] text-sm mb-4">
      The API request failed. Check your connection or try again.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#049fd9] text-white hover:bg-[#0380b0] transition-colors"
    >
      Retry
    </button>
  </div>
);
